import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRightLeft,
  CalendarCheck,
  CalendarX,
  CircleHelp,
  ClipboardCheck,
  EyeOff,
  HeartPulse,
  Lock,
  Pencil,
  PlusSquare,
  Search,
  ShieldAlert,
  Trash2,
  UserMinus,
  UserPlus,
  UserX,
  Users,
  Wrench,
} from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useOpsActivity } from '../api/useAdminOps';
import RunDetailDrawer from '../components/ops/RunDetailDrawer';
import {
  RUN_STATUS_STYLES,
  RefreshButton,
  StatusChip,
  TASK_LABELS,
  eventLabel,
  timeAgo,
} from '../components/ops/opsUi';

// Automation activity feed: what the runs actually DID, grouped per run —
// one icon-led line per audited action / raised decision, filterable by
// neighborhood (server-side) and action category (client-side). Clicking a
// run opens the full detail drawer.

const TASK_ICONS: Record<string, typeof Users> = {
  'match.initial': Users,
  'match.reoptimize': ArrowRightLeft,
  'match.finalize': ClipboardCheck,
  'reserve.book': CalendarCheck,
  'reserve.verify': Search,
  'reserve.release': CalendarX,
  'venue.research': Search,
};

type Category = 'seating' | 'tables' | 'decisions';

const CATEGORY_FILTERS: { key: Category; label: string }[] = [
  { key: 'seating', label: 'Seating' },
  { key: 'tables', label: 'Tables' },
  { key: 'decisions', label: 'Decisions' },
];

function actionCategory(action: string): Category {
  if (action.startsWith('assignment.') || action.startsWith('booking.')) return 'seating';
  return 'tables';
}

const ACTION_META: Record<string, { icon: typeof Users; color: string }> = {
  'assignment.create': { icon: UserPlus, color: 'text-emerald-500' },
  'assignment.move': { icon: ArrowRightLeft, color: 'text-blue-500' },
  'assignment.remove': { icon: UserMinus, color: 'text-amber-500' },
  'booking.cant_be_seated': { icon: UserX, color: 'text-red-500' },
  'booking.revived': { icon: HeartPulse, color: 'text-emerald-500' },
  'table.create': { icon: PlusSquare, color: 'text-emerald-500' },
  'table.update': { icon: Pencil, color: 'text-blue-500' },
  'table.delete': { icon: Trash2, color: 'text-red-500' },
  'table.lock': { icon: Lock, color: 'text-neutral-400' },
};

const DECISION_ICONS = {
  approval: ShieldAlert,
  question: CircleHelp,
  escalation: AlertTriangle,
} as const;

function tableLabel(a: AdminAPI.AuditLogEntry): string {
  if (a.restaurantName) {
    return `${a.restaurantName}${a.tableReservationName ? ` · "${a.tableReservationName}"` : ''}`;
  }
  // Deleted tables: the server recovers the venue from the audit metadata, but
  // if only the reservation name survived, that still beats a raw UUID.
  if (a.tableReservationName) return `table "${a.tableReservationName}"`;
  return a.availableTableId ? `table ${a.availableTableId.slice(0, 8)}` : 'table';
}

function describeAction(a: AdminAPI.AuditLogEntry): string {
  const who = a.subjectName || (a.bookingId ? `booking ${a.bookingId.slice(0, 8)}` : 'guest');
  const meta = (a.metadata ?? {}) as Record<string, unknown>;
  switch (a.action) {
    case 'assignment.create':
      return `Seated ${who} at ${tableLabel(a)}`;
    case 'assignment.move':
      return `Moved ${who} to ${tableLabel(a)}`;
    case 'assignment.remove':
      return `Unassigned ${who} from ${tableLabel(a)}`;
    case 'booking.cant_be_seated':
      return `Marked ${who} can't be seated (emailed + refunded)`;
    case 'booking.revived':
      return `Revived ${who} — seated at ${tableLabel(a)}`;
    case 'table.create':
      return `Created table at ${tableLabel(a)}${meta.capacity ? ` (capacity ${meta.capacity})` : ''}`;
    case 'table.update': {
      const changes = (meta.changes ?? {}) as Record<string, unknown>;
      const parts: string[] = [];
      if (changes.capacity !== undefined) parts.push(`capacity → ${changes.capacity}`);
      if (changes.restaurantId !== undefined) parts.push('restaurant changed');
      if (changes.reservationStatus !== undefined)
        parts.push(`reservation → ${changes.reservationStatus}`);
      if (changes.reservationName !== undefined) parts.push('reservation name changed');
      return `Updated ${tableLabel(a)}${parts.length ? `: ${parts.join(', ')}` : ''}${meta.changeReason ? ` — "${meta.changeReason}"` : ''}`;
    }
    case 'table.delete':
      return `Deleted ${tableLabel(a)}${meta.capacity ? ` (was capacity ${meta.capacity})` : ''}`;
    case 'table.lock':
      return `Locked ${tableLabel(a)} (guests notified)`;
    default:
      return `${a.action}${a.availableTableId ? ` — ${tableLabel(a)}` : ''}`;
  }
}

// Page-level visibility check (mirrors RunGroup's internal filtering) so
// time-section headers are only rendered when they have visible entries.
function isEntryVisible(
  entry: AdminAPI.OpsActivityRun,
  categories: Set<Category>,
  hideNoOps: boolean
): boolean {
  const { actions, decisions } = entry;
  if (hideNoOps && actions.length === 0 && decisions.length === 0) return false;
  if (categories.size > 0) {
    const anyAction = actions.some((a) => categories.has(actionCategory(a.action)));
    const anyDecision = categories.has('decisions') && decisions.length > 0;
    if (!anyAction && !anyDecision) return false;
  }
  return true;
}

const TIME_BUCKETS = [
  { label: 'Last 10 minutes', maxAgeMs: 10 * 60_000 },
  { label: 'Last hour', maxAgeMs: 60 * 60_000 },
  { label: 'Past day', maxAgeMs: 24 * 60 * 60_000 },
  { label: 'Last week', maxAgeMs: 7 * 24 * 60 * 60_000 },
  { label: 'Older', maxAgeMs: Infinity },
] as const;

function timeBucket(createdAt: string): string {
  const age = Date.now() - new Date(createdAt).getTime();
  for (const b of TIME_BUCKETS) if (age < b.maxAgeMs) return b.label;
  return 'Older';
}

function RunGroup({
  entry,
  categories,
  hideNoOps,
  onOpen,
}: {
  entry: AdminAPI.OpsActivityRun;
  categories: Set<Category>;
  hideNoOps: boolean;
  onOpen: (runId: string) => void;
}) {
  const { run, actions, decisions } = entry;
  if (hideNoOps && actions.length === 0 && decisions.length === 0) return null;
  const filtering = categories.size > 0;
  const visibleActions = filtering
    ? actions.filter((a) => categories.has(actionCategory(a.action)))
    : actions;
  const visibleDecisions = !filtering || categories.has('decisions') ? decisions : [];
  if (filtering && visibleActions.length === 0 && visibleDecisions.length === 0) return null;

  const TaskIcon = TASK_ICONS[run.taskType] ?? Wrench;
  const metrics = (run.metrics ?? {}) as { costUsd?: number };
  // No changes → the title row IS the whole entry.
  const empty = visibleActions.length === 0 && visibleDecisions.length === 0;

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
      <button
        onClick={() => onOpen(run.id)}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
          empty ? '' : 'border-b border-neutral-100 dark:border-neutral-800'
        }`}
        title="Open run detail"
      >
        <TaskIcon className={`w-4 h-4 shrink-0 ${empty ? 'text-neutral-400' : 'text-primary-500'}`} />
        <span className={`text-sm truncate ${empty ? 'text-dark-muted' : 'font-semibold'}`}>
          {TASK_LABELS[run.taskType] ?? run.taskType}
        </span>
        <span className="text-xs text-dark-muted truncate">
          {run.neighborhoodName ?? (run.neighborhoodId === null ? 'Event-wide' : run.neighborhoodId)}
        </span>
        <span
          className="text-[11px] text-dark-muted shrink-0 px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800"
          title={`Event ${run.eventTimeId ?? 'triage'}`}
        >
          {run.eventTimeId ? eventLabel(run.eventTimeId) : 'Triage'}
        </span>
        <StatusChip status={run.status} styles={RUN_STATUS_STYLES} />
        <span className="ml-auto text-[11px] text-dark-muted shrink-0">
          {empty && actions.length === 0 && decisions.length === 0 && 'no changes · '}
          {run.trigger} · {timeAgo(run.createdAt)}
          {typeof metrics.costUsd === 'number' && ` · $${metrics.costUsd.toFixed(2)}`}
        </span>
      </button>

      {!empty && (
      <div className="px-2.5 py-1.5 space-y-0.5">
        {visibleActions.map((a) => {
          const meta = ACTION_META[a.action] ?? { icon: Wrench, color: 'text-dark-muted' };
          const Icon = meta.icon;
          return (
            <div key={a.id} className="flex items-start gap-2 text-xs">
              <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.color}`} />
              <span className="min-w-0 break-words">{describeAction(a)}</span>
              <span className="ml-auto text-[10px] text-dark-muted shrink-0">
                {timeAgo(a.createdAt)}
              </span>
            </div>
          );
        })}
        {visibleDecisions.map((d) => {
          const Icon = DECISION_ICONS[d.kind] ?? ShieldAlert;
          return (
            <div key={d.id} className="flex items-start gap-2 text-xs">
              <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
              <span className="min-w-0 break-words">
                Raised {d.kind}: {d.title}
                <span className="text-dark-muted"> — {d.status}</span>
              </span>
              <span className="ml-auto text-[10px] text-dark-muted shrink-0">
                {timeAgo(d.createdAt)}
              </span>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default function AdminOpsActivity() {
  const [neighborhoodId, setNeighborhoodId] = useState<string>('');
  const [eventTimeId, setEventTimeId] = useState<string>('');
  const [categories, setCategories] = useState<Set<Category>>(new Set());
  const [hideNoOps, setHideNoOps] = useState(true);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const { data, isLoading, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useOpsActivity(neighborhoodId || undefined, eventTimeId || undefined);

  const entries = useMemo(
    () => data?.pages.flatMap((p) => p.data?.runs ?? []) ?? [],
    [data]
  );

  // Neighborhood options accumulate from what we've loaded (id → label).
  const neighborhoodOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entries) {
      if (e.run.neighborhoodId) {
        map.set(e.run.neighborhoodId, e.run.neighborhoodName ?? e.run.neighborhoodId);
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [entries]);

  // Event options accumulate from loaded runs, newest event first (the id
  // sorts chronologically: "YYYY-MM-DD_HH:mm").
  const eventOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const e of entries) if (e.run.eventTimeId) ids.add(e.run.eventTimeId);
    return [...ids].sort().reverse();
  }, [entries]);

  const toggleCategory = (key: Category) =>
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link href="/ops" className="text-dark-muted hover:text-primary-500">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Activity className="w-5 h-5 text-primary-500" />
            <h1 className="text-xl font-bold">Automation activity</h1>
          </div>
          <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <select
            value={neighborhoodId}
            onChange={(e) => setNeighborhoodId(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
            title="Filter by neighborhood"
          >
            <option value="">All neighborhoods</option>
            {neighborhoodOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={eventTimeId}
            onChange={(e) => setEventTimeId(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
            title="Filter by event"
          >
            <option value="">All events</option>
            {eventOptions.map((id) => (
              <option key={id} value={id}>
                {eventLabel(id)}
              </option>
            ))}
          </select>
          {CATEGORY_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => toggleCategory(f.key)}
              className={`px-2.5 py-1 rounded-full text-xs border ${
                categories.has(f.key)
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                  : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
              }`}
              title={`Show only ${f.label.toLowerCase()} actions (toggle)`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => setHideNoOps((v) => !v)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
              hideNoOps
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600'
                : 'border-neutral-300 dark:border-neutral-600 text-dark-muted hover:border-primary-400'
            }`}
            title="Completely hide runs that made no changes and raised no decisions"
          >
            <EyeOff className="w-3 h-3" />
            Hide no-ops
          </button>
        </div>

        {isLoading && <p className="text-sm text-dark-muted">Loading activity…</p>}
        {!isLoading && entries.length === 0 && (
          <p className="text-sm text-dark-muted">No automation runs yet.</p>
        )}

        {(() => {
          const visible = entries.filter((e) => isEntryVisible(e, categories, hideNoOps));
          const sections: { label: string; items: typeof visible }[] = [];
          for (const entry of visible) {
            const label = timeBucket(entry.run.createdAt);
            const last = sections[sections.length - 1];
            if (last && last.label === label) last.items.push(entry);
            else sections.push({ label, items: [entry] });
          }
          return sections.map((section) => (
            <section key={section.label} className="mb-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                {section.label}
              </h2>
              <div className="space-y-2">
                {section.items.map((entry) => (
                  <RunGroup
                    key={entry.run.id}
                    entry={entry}
                    categories={categories}
                    hideNoOps={hideNoOps}
                    onOpen={setOpenRunId}
                  />
                ))}
              </div>
            </section>
          ));
        })()}

        {hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="mt-4 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load older runs'}
          </button>
        )}
      </div>
      <RunDetailDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />
    </>
  );
}
